export const addRecurlyDependencies = () => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://js.recurly.com/v4/recurly.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = 'https://js.recurly.com/v4/recurly.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

let stripeDependencyPromise;

export const addStripeDependencies = () => {
  if (window.Stripe) {
    return Promise.resolve();
  }

  if (stripeDependencyPromise) {
    return stripeDependencyPromise;
  }

  const existingScript = document.querySelector('script[src="https://js.stripe.com/v3"]');

  stripeDependencyPromise = new Promise((resolve, reject) => {
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = 'https://js.stripe.com/v3';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return stripeDependencyPromise;
};

export const addSolidgateDependencies = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = 'https://cdn.solidgate.com/js/solid-form.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export const addPaddleDependencies = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = 'https://cdn.paddle.com/paddle/paddle.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const loadScript = (src, attributes = {}) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    // Collect.js reads its own <script> tag when it evaluates, so the
    // tokenization key must already be on the tag — and it must not be deferred
    // past the point where a mounted CardElement calls CollectJS.configure().
    script.async = true;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value != null) {
        script.setAttribute(name, value);
      }
    });
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

let collectJsPromise;
let gatewayJsPromise;

/**
 * Collect.js (TailoredPay / NMI) card fields. Unlike Stripe, the public key is
 * not passed to a constructor — it has to be a `data-tokenization-key`
 * attribute on the script tag at load time, so it cannot be swapped later
 * without a page reload. Both the URL and the key come from `GET /nmi/config`.
 */
export const addTailoredpayCollectDependencies = ({ collectJsUrl, tokenizationKey }) => {
  if (!collectJsUrl || !tokenizationKey) {
    return Promise.reject(new Error('[TailoredPay] collect_js_url or tokenization_key is missing'));
  }

  if (window.CollectJS) {
    return Promise.resolve();
  }

  if (!collectJsPromise) {
    // Only the tokenization key goes on the tag: Collect.js folds every `data-*`
    // attribute into its config and then rejects unknown keys with
    // "Unexpected fields", so no speculative extras here.
    collectJsPromise = loadScript(collectJsUrl, {
      'data-tokenization-key': tokenizationKey,
    }).catch((e) => {
      collectJsPromise = null;
      throw e;
    });
  }

  return collectJsPromise;
};

/**
 * Gateway.js — a SEPARATE script that provides payer authentication (3DS).
 * Collect.js cannot do 3DS on its own. The key here is a public API key with
 * Checkout permissions, distinct from the Collect.js tokenization key.
 */
export const addTailoredpayGatewayDependencies = ({ gatewayJsUrl }) => {
  if (!gatewayJsUrl) {
    return Promise.reject(new Error('[TailoredPay] gateway_js_url is missing'));
  }

  if (window.Gateway) {
    return Promise.resolve();
  }

  if (!gatewayJsPromise) {
    gatewayJsPromise = loadScript(gatewayJsUrl).catch((e) => {
      gatewayJsPromise = null;
      throw e;
    });
  }

  return gatewayJsPromise;
};
